/**
 * ServicesSection — Morgan Legal Group
 * Design: Cream background, staggered service cards with gold top border on hover
 * Asymmetric layout with section number watermarks
 */
import { useEffect, useRef, useState } from "react";
import { FileText, Scale, Shield, Users, Heart, Home, Gavel, ArrowRight, FileSignature, Building2, Stethoscope } from "lucide-react";

const services = [
  { icon: FileText, title: 'Estate Planning', description: 'Comprehensive estate plans tailored to your unique family situation. We craft wills, trusts, powers of attorney, and healthcare directives that protect your legacy and minimize tax exposure.', href: '/estate-planning/', featured: true },
  { icon: Gavel, title: 'Probate', description: 'Expert navigation of New York probate. We guide executors and beneficiaries through Surrogate&apos;s Court efficiently, minimizing delays and protecting the estate.', href: '/probate/', featured: false },
  { icon: Scale, title: 'Wills & Trusts', description: 'Precisely drafted wills and revocable or irrevocable trusts that ensure your assets are distributed according to your wishes while avoiding unnecessary probate costs.', href: '/trusts/', featured: false },
  { icon: Shield, title: 'Asset Protection', description: 'Strategic legal structures that shield your wealth from creditors, lawsuits, and unforeseen liabilities.', href: '/asset-protection/', featured: false },
  { icon: Users, title: 'Guardianship', description: 'Article 81 guardianship and special needs planning. Compassionate representation for incapacitated adults and vulnerable family members.', href: '/guardianship/', featured: false },
  { icon: FileSignature, title: 'Power of Attorney', description: 'Properly executed New York powers of attorney for financial and healthcare matters. Statutory short form plus enhanced provisions.', href: '/power-of-attorney/', featured: false },
  { icon: Heart, title: 'Elder Law', description: 'Medicaid planning, long-term care strategies, and protection against elder financial abuse. Specialized for New York&apos;s 5-year lookback.', href: '/elder-law/', featured: false },
  { icon: Home, title: 'Family Law', description: 'Sensitive representation in divorce, custody, child support, and matrimonial matters across the five boroughs and Long Island.', href: '/family-law/', featured: false },
  { icon: Building2, title: 'Real Estate', description: 'NYC residential and commercial real estate transactions, deed transfers, life estates, and property litigation.', href: '/real-estate/', featured: false },
  { icon: Stethoscope, title: 'Personal Injury', description: 'Accident, slip-and-fall, and medical malpractice claims. We handle insurance companies so you can focus on recovery.', href: '/personal-injury/', featured: false },
  { icon: Scale, title: 'Matrimonial', description: 'Complex matrimonial actions, prenuptial agreements, and post-divorce modifications. Discreet and strategic counsel.', href: '/matrimonial/', featured: false },
];

function ServiceCard({ service, index, visible }: { service: typeof services[0]; index: number; visible: boolean }) {
  const Icon = service.icon;
  const handleClick = () => {
    if (service.href.startsWith("#")) {
      const el = document.querySelector(service.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = service.href;
    }
  };

  return (
    <div
      className={`service-card group cursor-pointer transition-all duration-600 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${service.featured ? "lg:col-span-1 bg-navy text-white border-gold/30" : ""}`}
      style={{ transitionDelay: `${index * 80}ms` }}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className={`w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-200 ${
        service.featured
          ? "bg-gold/20 text-gold"
          : "bg-navy/8 text-navy group-hover:bg-gold/15 group-hover:text-gold"
      }`}>
        <Icon className="w-6 h-6" />
      </div>

      {/* Title */}
      <h3 className={`font-display text-xl font-bold mb-3 ${
        service.featured ? "text-white" : "text-navy"
      }`}>
        {service.title}
      </h3>

      {/* Description */}
      <p className={`font-body text-sm leading-relaxed mb-4 ${
        service.featured ? "text-white/70" : "text-navy/65"
      }`}>
        {service.description}
      </p>

      {/* CTA */}
      <div className={`flex items-center gap-2 text-sm font-medium font-body transition-all duration-200 group-hover:gap-3 ${
        service.featured ? "text-gold" : "text-navy/50 group-hover:text-gold"
      }`}>
        Learn More
        <ArrowRight className="w-4 h-4" />
      </div>

      {/* Featured gold accent line override */}
      {service.featured && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gold" />
      )}
    </div>
  );
}

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div ref={ref} className="max-w-2xl mb-14">
          <div className="section-label mb-4">Our Practice Areas</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            Comprehensive Legal Services for{" "}
            <span className="text-gold italic">New York Families</span>
          </h2>
          <p className="font-body text-navy/65 text-base lg:text-lg leading-relaxed">
            From drafting your first will to navigating complex probate proceedings, Morgan Legal Group provides the full spectrum of estate and family law services with unmatched expertise and personal attention.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger-children">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} visible={visible} />
          ))}
          {/* CTA card */}
          <div
            className={`service-card bg-gold border-gold flex flex-col justify-between cursor-pointer transition-all duration-600 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: `${services.length * 80}ms` }}
            onClick={() => {
              const el = document.querySelector("#consultation");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div>
              <h3 className="font-display text-navy text-xl font-bold mb-3">Not Sure Where to Start?</h3>
              <p className="font-body text-navy/75 text-sm leading-relaxed mb-6">
                Schedule a free 30-minute consultation. We&apos;ll assess your situation and recommend the right legal strategy.
              </p>
            </div>
            <div className="flex items-center gap-2 text-navy font-body text-sm font-semibold">
              Book Free Consultation
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
